import { useState, useCallback, useRef } from 'react';
import api from '../lib/api';

// Local storage key for alert settings
const ALERT_SETTINGS_KEY = 'geni_alert_settings';
// Session storage key for tracking which alerts have fired this session
const SESSION_FIRED_KEY = 'geni_alerts_fired_session';

// Default alert settings
const DEFAULT_SETTINGS = {
  losing_streak: { enabled: false, threshold: 3 },
  daily_drawdown: { enabled: false, threshold: 5 },
  weekly_drawdown: { enabled: false, threshold: 10 },
  overtrading: { enabled: false, threshold: 10 },
  revenge_trade: { enabled: false, threshold: 2 }, // threshold in hours
  winning_streak: { enabled: false, threshold: 3 }, // threshold in days
  max_open_positions: { enabled: false, threshold: 5 }, // threshold in positions
  daily_target: { enabled: false, threshold: 2 }, // threshold in percent
  weekly_target: { enabled: false, threshold: 5 }, // threshold in percent
  directional_exposure: { enabled: false, threshold: 60 }, // threshold in percent bias
  capital_drop: { enabled: false, threshold: 10 }, // threshold in percent drop from initial capital
  hold_time_drift: { 
    enabled: false, 
    trade_type: 'both', // 'day_trade', 'swing_trade', 'both'
    alert_near_win: true, // soft alert near average WIN hold time
    alert_past_loss: true // strong alert past average LOSS hold time
  }
};

/**
 * Load alert settings from localStorage
 */
const loadSettings = () => {
  try {
    const stored = localStorage.getItem(ALERT_SETTINGS_KEY);
    if (stored) {
      return { ...DEFAULT_SETTINGS, ...JSON.parse(stored) };
    }
  } catch (e) {
    console.error('Error loading alert settings:', e);
  }
  return DEFAULT_SETTINGS;
};

/**
 * Get fired alerts for this session
 */
const getSessionFired = () => {
  try {
    const stored = sessionStorage.getItem(SESSION_FIRED_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error('Error loading session fired alerts:', e);
  }
  return {};
};

/**
 * Mark an alert as fired for this session
 */
const markAsFired = (alertType) => {
  try {
    const current = getSessionFired();
    current[alertType] = true;
    sessionStorage.setItem(SESSION_FIRED_KEY, JSON.stringify(current));
  } catch (e) {
    console.error('Error saving session fired alert:', e);
  }
};

/**
 * Check if an alert has already fired this session
 */
const hasAlertFired = (alertType) => {
  const fired = getSessionFired();
  return fired[alertType] === true;
};

/**
 * Pulls the backend-persisted settings (enabled/threshold/email_enabled) for the 11
 * email-enabled alerts and merges them into the localStorage cache, backend fields winning
 * when present. hold_time_drift is never touched here - it stays frontend/localStorage only.
 * Best-effort: on any failure (no session, network) localStorage is left exactly as-is.
 */
export const syncAlertSettingsFromBackend = async () => {
  try {
    const response = await api.get('/trades-alerts/settings');
    const backendAlerts = response.data?.alerts;
    if (backendAlerts) {
      const current = loadSettings();
      const merged = { ...current };
      Object.keys(backendAlerts).forEach((key) => {
        merged[key] = { ...merged[key], ...backendAlerts[key] };
      });
      localStorage.setItem(ALERT_SETTINGS_KEY, JSON.stringify(merged));
    }
    return response.data;
  } catch (e) {
    return null;
  }
};

/**
 * Custom hook to manage trading alerts
 * Returns: { alerts, checkAlerts, dismissAlert }
 */
export function useAlerts() {
  const [alerts, setAlerts] = useState([]);
  const checkedRef = useRef(false); // Prevent multiple checks in same render cycle

  /**
   * Check all alert conditions and trigger if needed
   * @param {Object} params - Alert check parameters
   * @param {Array} params.trades - All user trades
   * @param {number} params.capital - Current user capital (legacy, used for daily/weekly drawdown)
   * @param {number} params.referenceCapital - Initial/starting capital for capital drop calculations
   * @param {number} params.currentCapital - Current calculated capital for capital drop calculations
   */
  const checkAlerts = useCallback((params) => {
    const { trades = [], capital = 0, referenceCapital = 0, currentCapital = 0 } = params;
    
    // Prevent duplicate checks in same session
    if (checkedRef.current) return;
    checkedRef.current = true;
    
    const settings = loadSettings();
    const newAlerts = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Get start of current week (Monday)
    const startOfWeek = new Date(today);
    const dayOfWeek = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
    startOfWeek.setDate(diff);

    // Filter to only closed trades with valid outcomes
    const closedTrades = trades.filter(t => 
      t.status === 'closed' && 
      ['win', 'loss', 'breakeven'].includes(t.outcome)
    );

    // Sort by closed_at date (most recent first) - with defensive date handling
    const sortedTrades = [...closedTrades]
      .filter(t => t.closed_at || t.created_at) // Only trades with valid timestamps
      .sort((a, b) => {
        const dateA = new Date(b.closed_at || b.created_at);
        const dateB = new Date(a.closed_at || a.created_at);
        if (isNaN(dateA.getTime())) return 1;
        if (isNaN(dateB.getTime())) return -1;
        return dateA - dateB;
      });

    // ============================================
    // CHECK 1: Losing Streak
    // ============================================
    if (settings.losing_streak.enabled && !hasAlertFired('losing_streak')) {
      const threshold = settings.losing_streak.threshold;
      let streak = 0;
      
      for (const trade of sortedTrades) {
        if (trade.outcome === 'loss') {
          streak++;
        } else {
          break; // Streak broken
        }
      }
      
      if (streak >= threshold) {
        markAsFired('losing_streak');
        newAlerts.push({
          id: `losing_streak_${Date.now()}`,
          type: 'losing_streak',
          title: `${streak} Consecutive Losses`,
          message: `You've hit ${streak} losing trades in a row. Consider taking a break or reviewing your strategy.`
        });
      }
    }

    // ============================================
    // CHECK 2: Daily Drawdown
    // ============================================
    if (settings.daily_drawdown.enabled && capital > 0 && !hasAlertFired('daily_drawdown')) {
      const threshold = settings.daily_drawdown.threshold;
      
      // Get today's trades - with defensive date handling
      const todayTrades = closedTrades.filter(t => {
        const dateStr = t.closed_at || t.created_at;
        if (!dateStr) return false;
        const tradeDate = new Date(dateStr);
        if (isNaN(tradeDate.getTime())) return false;
        tradeDate.setHours(0, 0, 0, 0);
        return tradeDate.getTime() === today.getTime();
      });
      
      // Calculate today's P&L
      const todayPnL = todayTrades.reduce((sum, t) => {
        return sum + (t.profit_usd || 0);
      }, 0);
      
      // Check if daily loss exceeds threshold
      if (todayPnL < 0) {
        const drawdownPercent = (Math.abs(todayPnL) / capital) * 100;
        
        if (drawdownPercent >= threshold) {
          markAsFired('daily_drawdown');
          newAlerts.push({
            id: `daily_drawdown_${Date.now()}`,
            type: 'daily_drawdown',
            title: `Daily Drawdown: ${drawdownPercent.toFixed(1)}%`,
            message: `You've lost ${drawdownPercent.toFixed(1)}% of your capital today. Your limit is ${threshold}%.`
          });
        }
      }
    }

    // ============================================
    // CHECK 3: Weekly Drawdown
    // ============================================
    if (settings.weekly_drawdown.enabled && capital > 0 && !hasAlertFired('weekly_drawdown')) {
      const threshold = settings.weekly_drawdown.threshold;
      
      // Get this week's trades - with defensive date handling
      const weekTrades = closedTrades.filter(t => {
        const dateStr = t.closed_at || t.created_at;
        if (!dateStr) return false;
        const tradeDate = new Date(dateStr);
        if (isNaN(tradeDate.getTime())) return false;
        return tradeDate >= startOfWeek;
      });
      
      // Calculate week's P&L
      const weekPnL = weekTrades.reduce((sum, t) => {
        return sum + (t.profit_usd || 0);
      }, 0);
      
      // Check if weekly loss exceeds threshold
      if (weekPnL < 0) {
        const drawdownPercent = (Math.abs(weekPnL) / capital) * 100;
        
        if (drawdownPercent >= threshold) {
          markAsFired('weekly_drawdown');
          newAlerts.push({
            id: `weekly_drawdown_${Date.now()}`,
            type: 'weekly_drawdown',
            title: `Weekly Drawdown: ${drawdownPercent.toFixed(1)}%`,
            message: `You've lost ${drawdownPercent.toFixed(1)}% of your capital this week. Your limit is ${threshold}%.`
          });
        }
      }
    }

    // ============================================
    // CHECK 4: Overtrading (Max Trades Per Day)
    // ============================================
    if (settings.overtrading.enabled && !hasAlertFired('overtrading')) {
      const threshold = settings.overtrading.threshold;
      
      // Get today's trades (including open ones) - with defensive date handling
      const todayAllTrades = trades.filter(t => {
        if (!t.created_at) return false;
        const tradeDate = new Date(t.created_at);
        if (isNaN(tradeDate.getTime())) return false;
        tradeDate.setHours(0, 0, 0, 0);
        return tradeDate.getTime() === today.getTime();
      });
      
      if (todayAllTrades.length >= threshold) {
        markAsFired('overtrading');
        newAlerts.push({
          id: `overtrading_${Date.now()}`,
          type: 'overtrading',
          title: `${todayAllTrades.length} Trades Today`,
          message: `You've placed ${todayAllTrades.length} trades today. Your limit is ${threshold}. Slow down and be selective.`
        });
      }
    }

    // ============================================
    // CHECK 5: Revenge Trade
    // Alert when re-entering same asset too soon after a loss
    // ============================================
    if (settings.revenge_trade.enabled && !hasAlertFired('revenge_trade')) {
      const thresholdHours = settings.revenge_trade.threshold;
      const thresholdMs = thresholdHours * 60 * 60 * 1000;
      
      // Find recent losing trades (closed with outcome='loss')
      // Use closed_at if available, otherwise fall back to updated_at or created_at
      const recentLosses = trades.filter(t => 
        t.status === 'closed' && 
        t.outcome === 'loss' &&
        (t.closed_at || t.updated_at || t.created_at) && // must have some timestamp
        t.asset // must have asset
      );
      
      // Find open trades (potential revenge trades)
      const openTrades = trades.filter(t => 
        t.status === 'open' &&
        t.created_at &&
        t.asset // must have asset
      );
      
      // Check for revenge trade pattern
      let revengeDetected = null;
      
      for (const openTrade of openTrades) {
        const openTradeAsset = (openTrade.asset || '').toLowerCase().trim();
        if (!openTradeAsset) continue;
        
        const openTradeCreatedAt = new Date(openTrade.created_at);
        if (isNaN(openTradeCreatedAt.getTime())) continue; // Skip invalid dates
        
        for (const lossTrade of recentLosses) {
          const lossAsset = (lossTrade.asset || '').toLowerCase().trim();
          if (!lossAsset) continue;
          
          // Check if same asset
          if (openTradeAsset !== lossAsset) continue;
          
          // Get loss close timestamp - prefer closed_at, fallback to updated_at, then created_at
          const lossTimestamp = lossTrade.closed_at || lossTrade.updated_at || lossTrade.created_at;
          const lossClosedAt = new Date(lossTimestamp);
          if (isNaN(lossClosedAt.getTime())) continue; // Skip invalid dates
          
          // Check if open trade was created AFTER loss AND within threshold
          const timeDiff = openTradeCreatedAt.getTime() - lossClosedAt.getTime();
          
          // Trigger if: new trade created after loss AND within threshold window
          // timeDiff > 0 means open trade was created after the loss
          // timeDiff <= thresholdMs means within the configured hours
          if (timeDiff > 0 && timeDiff <= thresholdMs) {
            const hoursSinceLoss = Math.round(timeDiff / (60 * 60 * 1000) * 10) / 10;
            revengeDetected = {
              asset: openTrade.asset,
              hoursSinceLoss
            };
            break;
          }
        }
        
        if (revengeDetected) break;
      }
      
      if (revengeDetected) {
        markAsFired('revenge_trade');
        newAlerts.push({
          id: `revenge_trade_${Date.now()}`,
          type: 'revenge_trade',
          title: 'Possible revenge trade detected',
          message: `You recently took a loss on ${revengeDetected.asset}. Consider reviewing before re-entering.`
        });
      }
    }

    // ============================================
    // CHECK 6: Winning Streak (Positive Alert)
    // Alert when user has consecutive profitable trading days
    // ============================================
    if (settings.winning_streak.enabled && !hasAlertFired('winning_streak')) {
      const thresholdDays = settings.winning_streak.threshold;
      
      // Group closed trades by day and calculate daily P&L
      const dailyPnL = {};
      
      closedTrades.forEach(trade => {
        const tradeDate = new Date(trade.closed_at || trade.updated_at || trade.created_at);
        const dayKey = tradeDate.toISOString().split('T')[0]; // YYYY-MM-DD
        
        if (!dailyPnL[dayKey]) {
          dailyPnL[dayKey] = 0;
        }
        dailyPnL[dayKey] += (trade.profit_usd || 0);
      });
      
      // Get days with trades, sorted by date (most recent first)
      const tradingDays = Object.entries(dailyPnL)
        .map(([date, pnl]) => ({ date, pnl }))
        .sort((a, b) => b.date.localeCompare(a.date));
      
      // Need at least 2 trading days to evaluate a streak
      if (tradingDays.length >= 2) {
        // Count consecutive winning days from most recent
        let winningStreak = 0;
        
        for (const day of tradingDays) {
          if (day.pnl > 0) {
            winningStreak++;
          } else {
            // Streak broken by a losing/breakeven day
            break;
          }
        }
        
        // Trigger if streak meets or exceeds threshold
        if (winningStreak >= thresholdDays) {
          markAsFired('winning_streak');
          newAlerts.push({
            id: `winning_streak_${Date.now()}`,
            type: 'winning_streak',
            title: "You're on a winning streak",
            message: `You're on a ${winningStreak}-day winning streak. Stay disciplined and avoid overconfidence.`
          });
        }
      }
    }

    // ============================================
    // CHECK 7: Max Open Positions
    // Alert when number of open positions is too high
    // ============================================
    if (settings.max_open_positions.enabled && !hasAlertFired('max_open_positions')) {
      const threshold = settings.max_open_positions.threshold;
      
      // Count open positions (status === 'open')
      const openPositions = trades.filter(t => t.status === 'open');
      const openCount = openPositions.length;
      
      // Trigger if open positions count reaches or exceeds threshold
      if (openCount >= threshold) {
        markAsFired('max_open_positions');
        newAlerts.push({
          id: `max_open_positions_${Date.now()}`,
          type: 'max_open_positions',
          title: 'Too many open positions',
          message: `You currently have ${openCount} open position${openCount !== 1 ? 's' : ''}. Your exposure may be getting harder to manage.`
        });
      }
    }

    // ============================================
    // CHECK 8: Daily Target Reached (Positive Alert)
    // Alert when daily profit target is reached
    // ============================================
    if (settings.daily_target.enabled && capital > 0 && !hasAlertFired('daily_target')) {
      const targetPercent = settings.daily_target.threshold;
      
      // Get today's closed trades
      const todayClosedTrades = closedTrades.filter(t => {
        const tradeDate = new Date(t.closed_at || t.updated_at || t.created_at);
        tradeDate.setHours(0, 0, 0, 0);
        return tradeDate.getTime() === today.getTime();
      });
      
      // Calculate today's realized P&L
      const todayPnL = todayClosedTrades.reduce((sum, t) => {
        return sum + (t.profit_usd || 0);
      }, 0);
      
      // Only trigger for positive P&L
      if (todayPnL > 0) {
        const dailyReturnPercent = (todayPnL / capital) * 100;
        
        // Trigger if daily return meets or exceeds target
        if (dailyReturnPercent >= targetPercent) {
          markAsFired('daily_target');
          newAlerts.push({
            id: `daily_target_${Date.now()}`,
            type: 'daily_target',
            title: 'Daily target reached',
            message: `You reached your ${targetPercent}% daily target. Consider protecting today's performance.`
          });
        }
      }
    }

    // ============================================
    // CHECK 9: Weekly Target Reached (Positive Alert)
    // Alert when weekly profit target is reached
    // ============================================
    if (settings.weekly_target.enabled && capital > 0 && !hasAlertFired('weekly_target')) {
      const targetPercent = settings.weekly_target.threshold;
      
      // Get this week's closed trades (reuse startOfWeek from earlier)
      const weekClosedTrades = closedTrades.filter(t => {
        const tradeDate = new Date(t.closed_at || t.updated_at || t.created_at);
        return tradeDate >= startOfWeek;
      });
      
      // Calculate week's realized P&L
      const weekPnL = weekClosedTrades.reduce((sum, t) => {
        return sum + (t.profit_usd || 0);
      }, 0);
      
      // Only trigger for positive P&L
      if (weekPnL > 0) {
        const weeklyReturnPercent = (weekPnL / capital) * 100;
        
        // Trigger if weekly return meets or exceeds target
        if (weeklyReturnPercent >= targetPercent) {
          markAsFired('weekly_target');
          newAlerts.push({
            id: `weekly_target_${Date.now()}`,
            type: 'weekly_target',
            title: 'Weekly target reached',
            message: `You reached your ${targetPercent}% weekly target. Consider preserving your weekly performance.`
          });
        }
      }
    }

    // ============================================
    // CHECK 10: Directional Exposure
    // Alert when open positions are too concentrated on one side
    // ============================================
    if (settings.directional_exposure.enabled && !hasAlertFired('directional_exposure')) {
      const thresholdPercent = settings.directional_exposure.threshold;
      
      // Get open positions with valid direction and hand_size
      const openPositions = trades.filter(t => 
        t.status === 'open' &&
        t.direction &&
        (t.direction === 'long' || t.direction === 'short') &&
        t.hand_size &&
        t.hand_size > 0
      );
      
      // Need at least 2 open positions to evaluate directional bias
      if (openPositions.length >= 2) {
        // Calculate long and short exposure
        let totalLongExposure = 0;
        let totalShortExposure = 0;
        
        openPositions.forEach(position => {
          const size = position.hand_size || 0;
          if (position.direction === 'long') {
            totalLongExposure += size;
          } else if (position.direction === 'short') {
            totalShortExposure += size;
          }
        });
        
        const totalExposure = totalLongExposure + totalShortExposure;
        
        // Avoid divide-by-zero
        if (totalExposure > 0) {
          const netDirectionalExposure = Math.abs(totalLongExposure - totalShortExposure);
          const directionalBiasPercent = (netDirectionalExposure / totalExposure) * 100;
          
          // Determine dominant side
          const dominantSide = totalLongExposure > totalShortExposure ? 'long' : 'short';
          
          // Trigger if bias exceeds threshold
          if (directionalBiasPercent >= thresholdPercent) {
            markAsFired('directional_exposure');
            
            const title = dominantSide === 'long' 
              ? 'High long-side exposure' 
              : 'High short-side exposure';
            const message = dominantSide === 'long'
              ? 'Most of your open exposure is concentrated on long positions.'
              : 'Most of your open exposure is concentrated on short positions.';
            
            newAlerts.push({
              id: `directional_exposure_${Date.now()}`,
              type: 'directional_exposure',
              title,
              message
            });
          }
        }
      }
    }

    // ============================================
    // CHECK 11: Capital Drop
    // Alert when current capital has dropped significantly from reference/initial capital
    // ============================================
    if (settings.capital_drop?.enabled && !hasAlertFired('capital_drop')) {
      const threshold = settings.capital_drop.threshold;
      
      // Safety checks - ensure we have valid capital values
      const hasValidReference = referenceCapital && referenceCapital > 0;
      const hasValidCurrent = currentCapital !== null && currentCapital !== undefined && currentCapital > 0;
      
      // Debug logging
      console.log("Capital Drop Alert Debug:", {
        referenceCapital,
        currentCapital,
        hasValidReference,
        hasValidCurrent,
        threshold
      });
      
      if (hasValidReference && hasValidCurrent) {
        // Only alert if current capital is BELOW reference (i.e., there's a loss)
        if (currentCapital < referenceCapital) {
          let dropPercent = ((referenceCapital - currentCapital) / referenceCapital) * 100;
          
          // Clamp to valid range
          if (dropPercent < 0) dropPercent = 0;
          if (dropPercent > 100) dropPercent = 100;
          
          console.log("Capital Drop Alert Debug - Drop calculated:", {
            dropPercent: dropPercent.toFixed(2),
            threshold,
            willTrigger: dropPercent >= threshold
          });
          
          if (dropPercent >= threshold) {
            markAsFired('capital_drop');
            newAlerts.push({
              id: `capital_drop_${Date.now()}`,
              type: 'capital_drop',
              title: `Capital down ${dropPercent.toFixed(1)}%`,
              message: `Your current capital has dropped ${dropPercent.toFixed(1)}% from your starting capital. Consider reviewing your risk management.`
            });
          }
        }
      }
    }

    // ============================================
    // CHECK 12: Hold-Time Drift
    // Alerts when an open position's duration approaches or exceeds
    // historical hold time baselines (win vs loss) for Day Trade or Swing Trade
    // ============================================
    if (settings.hold_time_drift?.enabled) {
      const htdSettings = settings.hold_time_drift;
      const tradeTypeFilter = htdSettings.trade_type || 'both';
      const alertNearWin = htdSettings.alert_near_win !== false;
      const alertPastLoss = htdSettings.alert_past_loss !== false;
      
      // Helper: Calculate hold time in milliseconds
      const getHoldTimeMs = (trade) => {
        const openTime = new Date(trade.open_time || trade.created_at);
        const closeTime = new Date(trade.closed_at || trade.close_time || trade.updated_at);
        return closeTime.getTime() - openTime.getTime();
      };
      
      // Get closed WIN and LOSS trades with valid hold times
      const closedWins = closedTrades.filter(t => 
        t.outcome === 'win' && 
        (t.open_time || t.created_at) && 
        (t.closed_at || t.close_time || t.updated_at)
      );
      
      const closedLosses = closedTrades.filter(t => 
        t.outcome === 'loss' && 
        (t.open_time || t.created_at) && 
        (t.closed_at || t.close_time || t.updated_at)
      );
      
      // Separate by trade type using STORED trade_type field
      const dayTradeWins = closedWins.filter(t => t.trade_type === 'day_trade');
      const dayTradeLosses = closedLosses.filter(t => t.trade_type === 'day_trade');
      const swingTradeWins = closedWins.filter(t => t.trade_type === 'swing_trade');
      const swingTradeLosses = closedLosses.filter(t => t.trade_type === 'swing_trade');
      
      // Helper: Calculate median hold time from array of trades
      const calcMedianHoldTime = (tradesArr) => {
        if (tradesArr.length === 0) return null;
        const holdTimes = tradesArr.map(t => getHoldTimeMs(t)).sort((a, b) => a - b);
        const mid = Math.floor(holdTimes.length / 2);
        return holdTimes.length % 2 !== 0 
          ? holdTimes[mid] 
          : (holdTimes[mid - 1] + holdTimes[mid]) / 2;
      };
      
      // Build trade types to check based on filter
      const tradeTypesToCheck = [];
      if (tradeTypeFilter === 'both' || tradeTypeFilter === 'day_trade') {
        tradeTypesToCheck.push({
          key: 'day_trade',
          wins: dayTradeWins,
          losses: dayTradeLosses
        });
      }
      if (tradeTypeFilter === 'both' || tradeTypeFilter === 'swing_trade') {
        tradeTypesToCheck.push({
          key: 'swing_trade',
          wins: swingTradeWins,
          losses: swingTradeLosses
        });
      }
      
      // Get open positions - ONLY ACTIVE trades (not pending)
      // Pending trades don't have duration tracking until activated
      const openPositions = trades.filter(t => 
        t.status === 'open' && 
        (t.position_status || 'active') === 'active' && // Only ACTIVE positions
        (t.activated_at || t.open_time || t.created_at) // Must have activation timestamp
      );
      
      const now = new Date();
      
      for (const openPos of openPositions) {
        // Use activated_at for duration (when position became active)
        // Fallback chain: activated_at -> open_time -> created_at (for backward compat)
        const timeSource = openPos.activated_at || openPos.open_time || openPos.created_at;
        if (!timeSource) continue; // Skip if no valid timestamp
        
        const openTime = new Date(timeSource);
        if (isNaN(openTime.getTime())) continue; // Skip invalid dates
        
        const currentHoldTimeMs = now.getTime() - openTime.getTime();
        const openPosTradeType = openPos.trade_type || 'day_trade';
        
        // Only check against the MATCHING trade type baseline
        for (const tradeTypeConfig of tradeTypesToCheck) {
          if (tradeTypeConfig.key !== openPosTradeType) continue;
          
          // Sample size gate
          if (tradeTypeConfig.wins.length < 5 || tradeTypeConfig.losses.length < 5) continue;
          
          const winBaseline = calcMedianHoldTime(tradeTypeConfig.wins);
          const lossBaseline = calcMedianHoldTime(tradeTypeConfig.losses);
          
          if (!winBaseline || !lossBaseline) continue;
          
          // Validity gate
          if (lossBaseline < winBaseline * 1.2) continue;
          
          const alertKeyBase = `${openPos._id || openPos.id}_${tradeTypeConfig.key}`;
          const softThreshold = winBaseline * 0.9;
          
          // Strong Alert: Past average LOSS hold time
          if (alertPastLoss && !hasAlertFired(`hold_time_drift_strong_${alertKeyBase}`)) {
            if (currentHoldTimeMs >= lossBaseline) {
              markAsFired(`hold_time_drift_strong_${alertKeyBase}`);
              newAlerts.push({
                id: `hold_time_drift_strong_${alertKeyBase}_${Date.now()}`,
                type: 'hold_time_drift',
                severity: 'strong',
                title: `Past losing hold time`,
                message: `This position has exceeded your average losing hold duration. You may be holding beyond your historical edge.`
              });
              continue;
            }
          }
          
          // Soft Alert: Near average WIN hold time (>= 90% of win baseline)
          if (alertNearWin && !hasAlertFired(`hold_time_drift_soft_${alertKeyBase}`)) {
            if (currentHoldTimeMs >= softThreshold && currentHoldTimeMs < lossBaseline) {
              markAsFired(`hold_time_drift_soft_${alertKeyBase}`);
              newAlerts.push({
                id: `hold_time_drift_soft_${alertKeyBase}_${Date.now()}`,
                type: 'hold_time_drift',
                severity: 'soft',
                title: `Approaching winner hold time`,
                message: `This position is nearing your average winner hold duration. Review whether the setup still supports holding.`
              });
            }
          }
        }
      }
    }

    // Update alerts state
    if (newAlerts.length > 0) {
      setAlerts(newAlerts);
    }
  }, []);

  /**
   * Dismiss a specific alert
   */
  const dismissAlert = useCallback((alertId) => {
    setAlerts(prev => prev.filter(a => a.id !== alertId));
  }, []);

  /**
   * Reset the check flag (for testing or page refresh scenarios)
   */
  const resetCheck = useCallback(() => {
    checkedRef.current = false;
  }, []);

  return {
    alerts,
    checkAlerts,
    dismissAlert,
    resetCheck
  };
}
